import type { TenantRole } from '@servicetim/shared';

const RANK: Record<TenantRole, number> = { guest: 0, member: 1, admin: 2, owner: 3 };

/** True when `role` is at least as privileged as `minimum` (mirrors the API's `hasTenantRole`). */
export const hasTenantRole = (role: TenantRole | null | undefined, minimum: TenantRole): boolean =>
  role != null && RANK[role] >= RANK[minimum];

/** Claims the UI keeps from the token (or dev claims); the server re-verifies every request. */
export interface SessionClaims {
  tenantId: string;
  userId: string;
  tenantRole: TenantRole;
}

/** Placeholder scope when auth is disabled and no claims exist at all. */
export const LOCAL_CLAIMS: SessionClaims = {
  tenantId: 'local',
  userId: 'local',
  tenantRole: 'owner',
};

const TENANT_ROLES: readonly TenantRole[] = ['owner', 'admin', 'member', 'guest'];

/**
 * Reads `tenant_id` / `user_id` / `tenant_role` from a JWT payload or a claims object without
 * verifying anything: the values only drive navigation; every API call is authorized server-side.
 */
export function claimsFromObject(raw: unknown): SessionClaims | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const tenantId = record.tenant_id;
  const userId = record.user_id;
  const tenantRole = record.tenant_role;
  if (typeof tenantId !== 'string' || typeof userId !== 'string') return null;
  if (typeof tenantRole !== 'string' || !TENANT_ROLES.includes(tenantRole as TenantRole)) {
    return null;
  }
  return { tenantId, userId, tenantRole: tenantRole as TenantRole };
}

/** Decodes the payload segment of a compact JWT; `null` when the token is not a JWT. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const padded = segment
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(segment.length / 4) * 4, '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
